import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { formatDate, removeTime } from "../../../Utils/titleCaseFunction";
import { errorMessages } from "../../../Helpers/ErrorMessages";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { history } from "../../../Helpers/history";


// =============================
// API FUNCTIONS
// =============================
const fetchPurchaseData = async (purchaseId) => {
  try {
    const response = await axios.get(
      `${config.epharmUrl}/Cpurchase/purchase_details_data`,
      { params: { purchase_id: purchaseId } }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching purchase data:", error);
    throw error;
  }
};

const updatePurchaseData = async (payload) => {
  try {
    const response = await axios.put(
      `${config.epharmUrl}/Cpurchase/update_purchase_full`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating purchase data:", error);
    throw error;
  }
};

const fetchSuppliers = async () => {
  try {
    const response = await axios.get(
      `${config.epharmUrl}/Cmanufacturer/retrieve_all_manufacturers`
    );
    return response.data.total_manufacturers || [];
  } catch (error) {
    console.error("Error fetching suppliers:", error);
    throw error;
  }
};

// =============================
// VALIDATION
// =============================
const validatePurchase = ({ rows, manufacturer_name, invoice_no, purchase_date }) => {
  if (!manufacturer_name?.manufacturer_name) {
    return { valid: false, message: "Please select a supplier." };
  }

  if (!invoice_no?.trim()) {
    return { valid: false, message: "Invoice number cannot be empty." };
  }

  if (!purchase_date?.trim()) {
    return { valid: false, message: "Please select a purchase date." };
  }

  if (!rows || rows.length === 0) {
    return { valid: false, message: "No products added to the purchase." };
  }

  for (let i = 0; i < rows.length; i++) {
    const item = rows[i];
    if (!item.product_name ) {
      return { valid: false, message: `Row ${i + 1} is product Name is empty .` };
    }
    if (!item.batch_id?.toString()?.trim() ) {
        return { valid: false, message: `Row ${i + 1}  Batch Number is incomplete.` };
      }
    if (item.product_quantity == null || item.product_quantity === "") {
    return { valid: false, message: `Row ${i + 1} product quantity cannot be empty .` };
    }
    if (item.product_rate == null || item.product_rate === "") {
      return { valid: false, message: `Row ${i + 1}: Unit cost cannot be empty.` };
    }
  }

  return { valid: true };
};

// =============================
// CUSTOM HOOK
// =============================
export const useEditPurchase = (actions, purchaseId) => {
  const [rows, setRows] = useState([]);
  const [manufs, setManufs] = useState([]);
  const [manufacturer_name, setManufacturerName] = useState(null); // keep as object
  const [manufacturer_id, setManufacturerID] = useState("");
  const [old_manufacturer, setOldManufacturer] = useState("");


  const [invoice_no, setInvoiceNo] = useState("");
  const [old_invoice_no, setOldInvoiceNo] = useState("");
  const [purchase_date, setPurchaseDate] = useState("");
  const [old_purchase_date, setOldPurchaseDate] = useState("");
  const [expected_payment_date, setExpectedPaymentDate] = useState("");
  const [payment_method, setPaymentMethod] = useState("");
  const [paid_amount, setPaidAmount] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [grand_total, setGrandTotal] = useState(0);
  const [new_supplier, setNewSupplier] = useState(null);
  const [loading, setLoading] = useState(true);   // ← Add this

  // =============================
  // LOAD PURCHASE DATA
  // =============================
  useEffect(() => {
    if (!purchaseId) return;

    const loadPurchase = async () => {
      setLoading(true);        // ← Start loading
      try {
        const suppliers = await fetchSuppliers();
        setManufs(suppliers);

        const data = await fetchPurchaseData(purchaseId);

        // Find supplier object
        const selectedSupplier = suppliers.find(
          (sup) => sup.manufacturer_name === data.manufacturer_name
        ) || { manufacturer_name: data.manufacturer_name, manufacturer_id: data.manufacturer_id };

        setManufacturerName(selectedSupplier);
        setManufacturerID(selectedSupplier.manufacturer_id || "");
        setOldManufacturer(selectedSupplier);

        setInvoiceNo(data.chalan_no || "");
        setOldInvoiceNo(data.chalan_no || "");
        setPurchaseDate(data.purchase_date ? formatDate(data.purchase_date) : "");
        setOldPurchaseDate(data.purchase_date ? formatDate(data.purchase_date) : "");
        setExpectedPaymentDate(data.final_date ? formatDate(data.final_date) : "");
        setPaymentMethod(data.payment_type || "");
        setPaidAmount(data.sub_total_amount || 0);

        // Map product details
        const productDetails = data.purchase_all_data || [];
        const mappedProducts = productDetails.map((item) => ({
          ...item,
          product_name: item.product_name,
          product_id: item.product_id,
          batch_id: item.batch_id,
          old_batch_id: item.batch_id,
          expiry_date: item.expeire_date ? removeTime(item.expeire_date) : "",
          old_expiry_date: item.expeire_date ? removeTime(item.expeire_date) : "",
          product_quantity: item.quantity || 0,
          old_quantity: item.quantity || "",
          product_rate: item.manufacturer_price || 0,
          old_unit_cost: item.manufacturer_price || "",
          total_price: item.total_amount || 0,
          wholesale_price: item.wholesale_price || 0,
          retail_price: item.retail_price || 0,
          balance: item.balance || 0,
          quantity_sold: (item.quantity || 0) - (item.remaining_stock || 0),
          expiry_limit: item.expiry_limit || 0,
          purchase_details_ids: item.purchase_details_ids,
          uuid_id: uuidv4(),
          qty_sold: item.qty_sold,
        }));

        setRows(mappedProducts);
        setGrandTotal(mappedProducts.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0));
      } catch (error) {
        errorMessages(error, null, actions);
      }
      finally {
        setLoading(false);   // ← End loading
      } 
    };

    loadPurchase();
  }, [purchaseId]);

  // =============================
  // HANDLERS
  // =============================
  const handleChangeValue = (e) => {
    const { name, value } = e.target;
    if (name === "invoice_no") setInvoiceNo(value);
    if (name === "purchase_date") setPurchaseDate(value);
    if (name === "expected_payment_date") setExpectedPaymentDate(value);
  };

  const handleChangeManufacturer = (val) => {
    // If nothing selected, clear the values
    if (!val) {
      setNewSupplier(null);
      setManufacturerName(null);
      return;
    }
  
    // Only set new supplier if the selected one is different
    if (val.manufacturer_id !== manufacturer_id) {
      setNewSupplier(val);
    } else {
      setNewSupplier(null);
    }
  
    setManufacturerName(val);
  };
  
  const handleChangeQuantity = (e, idx) => {
    const newQty = e.target.value;
    const updatedRows = [...rows];
    updatedRows[idx].product_quantity = newQty;
    updatedRows[idx].new_quantity = newQty;
    updatedRows[idx].total_price = (parseFloat(updatedRows[idx].product_rate) || 0) * (parseFloat(newQty) || 0);
    setRows(updatedRows);
    updateGrandTotal(updatedRows);
  };

  const handleChangePrice = (e, idx) => {
    const newPrice = e.target.value;
    const updatedRows = [...rows];
    updatedRows[idx].product_rate = newPrice;
    updatedRows[idx].new_unit_cost = newPrice;
    updatedRows[idx].total_price = (parseFloat(newPrice) || 0) * (parseFloat(updatedRows[idx].product_quantity) || 0);
    setRows(updatedRows);
    updateGrandTotal(updatedRows);
  };

  const handleChangeExpiryDate = (e, idx) => {
    const newDate = e.target.value;
    const updatedRows = [...rows];
    updatedRows[idx].new_expiry_date = newDate;
    updatedRows[idx].expiry_date = newDate;
    setRows(updatedRows);
  };

  const handleChangePaymentMethod = (e) => setPaymentMethod(e.target.value);
  const handleChangePaidAmount = (e) => setPaidAmount(e.target.value);

  const handleChangeBatch = (e, idx) => {
    const newBatch = e.target.value;
    const updatedRows = [...rows];
    updatedRows[idx].batch_id = newBatch;
    updatedRows[idx].new_batch_id = newBatch;
    setRows(updatedRows);
  };

  const updateGrandTotal = (items) => {
    setGrandTotal(items.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0));
  };
   const handleCloseSnackbar = () => {
    actions.snackbarActions.hideSnackBar();
  };

  // =============================
  // SUBMIT HANDLER
  // =============================
  const handleSubmitPurchase = async () => {
    setSubmitted(true);

    const validation = validatePurchase({ rows, manufacturer_name, invoice_no, purchase_date });
    if (!validation.valid) {
    //   actions.snackbarActions.infoSnackbar(validation.message);
    actions.snackbarActions.infoSnackbar(validation.message);
    setSubmitted(false);
      return;
    }

    const supplierChanged = new_supplier?.manufacturer_id && new_supplier.manufacturer_id !== old_manufacturer.manufacturer_id;
    const invoiceChanged = old_invoice_no !== invoice_no;
    const dateChanged = old_purchase_date !== purchase_date;
    const changedItems = rows.filter(item =>
      item.old_quantity !== item.product_quantity ||
      item.old_unit_cost !== item.product_rate ||
      item.old_expiry_date !== (item.new_expiry_date || item.expiry_date) ||
      (item.new_batch_id && item.old_batch_id !== item.new_batch_id)
    );

    if (!supplierChanged && !invoiceChanged && !dateChanged && changedItems.length === 0) {
      actions.snackbarActions.infoSnackbar("No changes detected to update.");
      return;
    }

    const payload = {
      purchase_id: purchaseId,
      old_manufacturer_id: old_manufacturer.manufacturer_id,
      new_manufacturer_id: new_supplier?.manufacturer_id || manufacturer_name.manufacturer_id,
      old_invoice_no,
      new_invoice_no: invoice_no,
      old_purchase_date,
      new_purchase_date: purchase_date,
      paid_amount,
      items: rows.map(item => ({
        product_id: item.product_id,
        batch_id: item.batch_id,
        old_batch_id: item.old_batch_id,
        new_batch_id: item.new_batch_id || item.batch_id,
        old_quantity: item.old_quantity,
        new_quantity: item.product_quantity,
        old_unit_cost: item.old_unit_cost,
        new_unit_cost: item.product_rate,
        old_expiry_date: item.old_expiry_date,
        new_expiry_date: item.new_expiry_date || item.expiry_date,
        total_price: item.total_price,
        wholesale_price: item.wholesale_price,
        retail_price: item.retail_price,
        purchase_details_ids: item.purchase_details_ids,
      })),
    };

    try {
      const result = await updatePurchaseData(payload);
      if (result.success) {
        // actions.snackbarActions.successSnackbar("Purchase updated successfully.");
        actions.snackbarActions.successSnackbar( "Purchase updated successfully.");
        
        history.push(`/managepurchase`);


      } else {
        // actions.snackbarActions.infoSnackbar();
        actions.snackbarActions.infoSnackbar( "Failed to update purchase. Please try again.");
      }
    } catch (error) {
      actions.snackbarActions.infoSnackbar("An error occurred while updating the purchase.");
    }
  };

  return {
    rows,
    manufacturer_name,
    handleChangeBatch,
    manufs,
    invoice_no,
    purchase_date,
    expected_payment_date,
    payment_method,
    paid_amount,
    grand_total,
    submitted,
    handleChangeValue,
    handleChangeManufacturer,
    handleChangeQuantity,
    handleChangePrice,
    handleChangeExpiryDate,
    handleChangePaymentMethod,
    handleChangePaidAmount,
    manufacturer_id,
    handleSubmitPurchase,
    loading,
    handleCloseSnackbar,
  };
};
